import * as React from 'react';
import { CloseOnBlur } from '../close-on-blur';
import { DropdownMenuContext, DropdownMenuProps, DropdownMenuState } from './types';
import { ButtonSecondary } from 'ui';
import { OnClickOutside } from '../on-click-outside';

import styles from './dropdown-menu.scss';
import { Handlers, HotKeys, KeyMap } from 'ui/common/hotkeys';
import { keyCodeTypes } from 'ui/common/key-code-types';
import { createPortal } from 'react-dom';
import { CloseOnScroll } from 'ui/common/close-on-scroll';

export const DropDownContext = React.createContext<DropdownMenuContext>({
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  handleDropdownHide: () => { }
});

export class DropdownMenu extends React.Component<DropdownMenuProps, DropdownMenuState> {
  public dropdownRef = React.createRef<HTMLDivElement>();
  public menuItemRef = React.createRef<HTMLDivElement>();
  private handleHide: () => void;

  constructor(props: DropdownMenuProps) {
    super(props);

    this.handleHide = () => {
      if (this.state.dropdownExpanded) {
        this.setState({
          dropdownExpanded: false
        });
      }
    };

    this.state = {
      dropdownExpanded: false,
      top: 0,
      right: 0,
      handleDropdownHide: this.handleHide
    };
  }

  public componentDidMount() {
    this.addListeners();
  }

  public componentWillUnmount() {
    this.removeListeners();
  }
  
  private addListeners () {
    window.addEventListener('resize', this.handleHide);
  }

  private removeListeners () {
    window.removeEventListener('resize', this.handleHide);
  }

  public componentDidUpdate(prevProps: DropdownMenuProps, prevState: DropdownMenuState) {
    if (!!this.props.onExpandMenu && this.state.dropdownExpanded && !prevState.dropdownExpanded && !this.props.closeOnDemand) {
      this.props.onExpandMenu();
    }

    if (!!this.props.onCloseMenu && !this.state.dropdownExpanded && prevState.dropdownExpanded) {
      this.setState({ offsetX: 0 });
      this.props.onCloseMenu();
    }

    if (this.props.closeOnDemand === false && !prevProps.closeOnDemand === false) {
      this.setState({ offsetX: 0, dropdownExpanded: false });
    }
  }

  private handleClick = (e: React.MouseEvent<any>) => {
    e.preventDefault();
    const { portalTarget } = this.props;
    if (portalTarget) {
      const rect = e?.currentTarget?.getBoundingClientRect() || { top: 0, right: 0 };
      const parentRect = portalTarget?.getBoundingClientRect() || { top: 0, right: 0 };
      const top = (rect.top - parentRect.top) +
        this.menuItemRef.current?.offsetHeight + 6 || 0; // 6px for small gap between button and dropdown
      const positionRight = parentRect.right - rect.right;
      const right = positionRight > 0 ? positionRight : 0;
      this.setState({
        dropdownExpanded: !this.state.dropdownExpanded,
        top,
        right
      });
    } else {
      this.setState({
        dropdownExpanded: !this.state.dropdownExpanded
      });
    }
  };

  private keyMap: KeyMap = {
    escapeKey: [keyCodeTypes.Esc]
  };

  private handlerHotkeys: Handlers = {
    escapeKey: () => this.handleHide()
  };

  private onOpenDone = (e: React.AnimationEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.left < 0) {
      this.setState({ offsetX: -rect.left });
    }
  };

  private setFocus = (element: HTMLElement) => {
    element?.focus();
  };

  public render() {
    const { portalTarget } = this.props;
    const { dropdownExpanded, offsetX, top: top, right: right } = this.state;
    const onClickHandler = !!this.props.closeAfterClick ? this.handleHide : undefined;
    const positionStyle = portalTarget ? { top, right } : {};
    const adjustmentStyle = offsetX ? { ...positionStyle, transform: `translateX(${offsetX}px)` } : { ...positionStyle };

    const menuItem =
      <div className="dropdown-trigger" ref={this.menuItemRef}>
        <ButtonSecondary onClick={this.handleClick}>
          {this.props.dropdownNode}
        </ButtonSecondary>
      </div>;
    const dropdownList = (
      <ul
        ref={this.setFocus}
        tabIndex={-1}
        className={`dropdown-menu ${dropdownExpanded ? 'show' : ''} ${styles.dropdownMenu}`}
        onClick={onClickHandler}
        onAnimationEnd={this.onOpenDone}
        style={adjustmentStyle}
      >
        {this.props.children}
      </ul>
    );
    return (
      <div id={this.props.id} className={`dropdown-wrapper ${styles.dropdownWrapper} ${dropdownExpanded ? 'expanded' : ''}`}>
        {dropdownExpanded ? (
          <DropDownContext.Provider value={this.state}>
            <HotKeys keyMap={this.keyMap} handlers={this.handlerHotkeys}>
              <OnClickOutside onClickOutside={this.handleHide} dropdownRef={this.dropdownRef}>
                <CloseOnBlur onBlur={this.handleHide} dropdownRef={this.dropdownRef}>
                <CloseOnScroll parentElement={portalTarget} onScroll={this.handleHide}>
                  <div className={'dropdown'}>
                    {menuItem}
                    {portalTarget ? (
                      createPortal(
                        <div ref={this.dropdownRef} className='portal-wraper'>
                          {dropdownList}
                        </div>,
                        portalTarget
                      )
                    ) : (
                      dropdownList
                    )}
                  </div>
                </CloseOnScroll>
                </CloseOnBlur>
              </OnClickOutside>
            </HotKeys>
          </DropDownContext.Provider>
        ) : (
          <div className="dropdown">{menuItem}</div>
        )}
      </div>
    );
  }
}
